import { isAxiosError } from 'axios';

const LIMIT_MESSAGE = "You've reached the pin limit. Unpin a list first.";
const GENERIC_MESSAGE = 'Failed to update pin. Please try again.';

export interface PinErrorToast {
  title: string;
  status: 'warning' | 'error';
  /** Stable toast id — pass to `toast.isActive`/`toast()` so repeated taps don't stack. */
  id: 'pin-limit' | 'pin-error';
}

/**
 * Maps a pin/unpin mutation error to member-facing toast copy.
 *
 * Gotchas:
 * - The 401 refresh interceptor (src/lib/apiClient.ts) rejects with the refresh
 *   request's own error, not the original request's error — a session-expiry
 *   failure won't carry the original 400, so it correctly falls through to
 *   generic copy here rather than misreporting as a limit hit.
 * - `pin_to_profile()` (paire_api/views/list.py) returns 400 ONLY for the cap —
 *   403 for non-owner, 404 for missing list — so `status === 400` alone is
 *   sufficient without matching the response body string. The interceptor
 *   camelizes error bodies, but the backend sends a bare string here, so
 *   there's no transformation surprise either way. `unpin_from_profile` never
 *   returns 400, so this naturally falls through to generic copy for unpin.
 */
export function getPinErrorToast(error: unknown): PinErrorToast {
  if (isAxiosError(error) && error.response?.status === 400) {
    return { title: LIMIT_MESSAGE, status: 'warning', id: 'pin-limit' };
  }
  return { title: GENERIC_MESSAGE, status: 'error', id: 'pin-error' };
}
